import { AppComponentProps, NumberKeyedDictionary } from '../typescriptHelpers';
import * as React from 'react';
import { ReleaseTagPattern, DisallowedTokensForTag, RequireAtLeastOneOfTokensForTag } from '../types';
import {
    NewReleaseTagPatternRequest,
    DeleteReleaseTagPatternRequest,
    UpdateReleaseTagPatternRequest,
    tryUpdateAppSettings,
} from '../actions/appSettings';
import { PlusOutlined } from '@ant-design/icons';
import { RootState } from '../reducers/reducers';
import { getError, getIsLoading, getUpdated, validatePattern } from '../helpers';
import { AppSettingsUpdatable } from '../actions/updatable';
import { connect, ConnectedProps } from 'react-redux';
import NewReleaseTagPatternModal from './NewReleaseTagPatternModal';
import { forEach, forIn } from 'lodash';
import { AppSettingsEditableInputComponent, AppSettingsActionsComponent } from './AppSettingsTableActionsComponent';
import styles from './styles/settingsStyles';
import Text from 'antd/lib/typography/Text';
import Table from 'antd/lib/table';
import { RunwayUserAction } from '../helpers/aclHelpers';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { selectApp, selectAuthCredentials } from '../selectors/selectors';
import { globalMessage } from '../utils/globalMessage';
import { AppEntityRequest } from '../actions/entities';
import * as API from '../api';
import Typography from 'antd/lib/typography';
import Space from 'antd/lib/space';

interface State {
    newReleaseTagModalVisible: boolean;
    releaseTagIsEditing: NumberKeyedDictionary<boolean>;
    releaseTagPatternUpdated: NumberKeyedDictionary<string>;
    releaseTagPatternIsValid: NumberKeyedDictionary<boolean>;
    suggestedTagPattern: string;
    isSuggestedTagPatternLoading: boolean;
}

class ReleaseTagPatternsComponent extends React.Component<Props, State> {
    constructor(props) {
        super(props);

        this.onAddNewReleaseTagClicked.bind(this);
        this.confirmReleaseTagPatternDelete.bind(this);
        this.addNewReleaseTag.bind(this);

        this.state = {
            newReleaseTagModalVisible: false,
            releaseTagIsEditing: {},
            releaseTagPatternUpdated: {},
            releaseTagPatternIsValid: {},
            suggestedTagPattern: '',
            isSuggestedTagPatternLoading: false,
        };
    }

    static getDerivedStateFromProps(nextProps: Props, prevState: State) {
        const updatedReleaseTagPatternUpdated = {
            ...prevState.releaseTagPatternUpdated,
        };

        const updatedReleaseTagPatternIsEditing = {
            ...prevState.releaseTagIsEditing,
        };

        forIn(nextProps.releaseTagPatternUpdated, (didUpdate, id) => {
            if (didUpdate) {
                // clear stored pattern updates
                // reset field to isEditing = false
                updatedReleaseTagPatternUpdated[id] = null;
                updatedReleaseTagPatternIsEditing[id] = false;
            }
        });

        return {
            newReleaseTagModalVisible:
                nextProps.newReleaseTagPatternAdded ? false : prevState.newReleaseTagModalVisible,
            releaseTagPatternUpdated: updatedReleaseTagPatternUpdated,
            releaseTagIsEditing: updatedReleaseTagPatternIsEditing,
        };
    }

    componentDidUpdate(prevProps: Props) {
        if (!prevProps.newReleaseTagPatternError && this.props.newReleaseTagPatternError) {
            globalMessage.error(this.props.newReleaseTagPatternError);
        }

        if (!prevProps.newReleaseTagPatternAdded && this.props.newReleaseTagPatternAdded) {
            globalMessage.success('New tag pattern added!');
        }

        // Error updating pattern
        forIn(this.props.releaseTagPatternUpdateError, (error, id) => {
            const prevError = prevProps.releaseTagPatternUpdateError[id];

            if (!prevError && error) {
                globalMessage.error(error);
            }
        });
    }

    onAddNewReleaseTagClicked = () => {
        this.setState({
            ...this.state,
            newReleaseTagModalVisible: true,
        });

        if (!this.state.suggestedTagPattern) {
            this.loadSuggestedTagPattern();
        }
    };

    onCancelAddNewReleaseTag = () => {
        this.setState({
            ...this.state,
            newReleaseTagModalVisible: false,
        });
    };

    addNewReleaseTag = (pattern: string) => {
        const newReleaseTagPatternRequest: NewReleaseTagPatternRequest = {
            pattern,
            orgId: this.props.orgId,
            appId: this.props.appId,
        };

        this.props.addNewReleaseTag(newReleaseTagPatternRequest);
    };

    confirmReleaseTagPatternDelete = (id: number) => {
        const deleteReleaseTagPatternRequest: DeleteReleaseTagPatternRequest = {
            orgId: this.props.orgId,
            appId: this.props.appId,
            tagPatternId: id,
        };

        this.props.deleteReleaseTagPattern(deleteReleaseTagPatternRequest);
    };

    saveReleaseTagPattern = (id: number) => {
        const saveReleaseTagPatternRequest: UpdateReleaseTagPatternRequest = {
            orgId: this.props.orgId,
            tagPatternId: id,
            pattern: this.state.releaseTagPatternUpdated[id],
            appId: this.props.appId,
        };

        this.props.updateReleaseTagPattern(saveReleaseTagPatternRequest, id);
    };

    loadSuggestedTagPattern = () => {
        const request: AppEntityRequest = {
            orgId: this.props.orgId,
            appId: this.props.appId,
        };

        this.setState({
            ...this.state,
            newReleaseTagModalVisible: true,
            isSuggestedTagPatternLoading: true,
        });

        API.getSuggestedReleaseTagPattern(request, this.props.credentials!)
            .then((response) => {
                this.setState({
                    ...this.state,
                    suggestedTagPattern: response,
                });
            })
            .catch(() => {
                return;
            })
            .finally(() => {
                this.setState({
                    ...this.state,
                    isSuggestedTagPatternLoading: false,
                });
            });
    };

    renderReleaseTagPatternField: (record: ReleaseTagPattern) => JSX.Element = (record) => {
        const releaseTagEditing = this.state.releaseTagIsEditing[record.id];

        return (
            <AppSettingsEditableInputComponent
                id={record.id}
                isEditing={releaseTagEditing}
                defaultValue={record.pattern}
                textProps={{ code: true }}
                rules={[
                    {
                        validator: (rule, value) => {
                            const pattern = value as string;

                            return validatePattern(
                                pattern,
                                'Tag',
                                RequireAtLeastOneOfTokensForTag,
                                DisallowedTokensForTag,
                            );
                        },
                    },
                ]}
                onValueChanged={(id, value, isValid) => {
                    const releaseTagPatternUpdated = {
                        ...this.state.releaseTagPatternUpdated,
                        [id]: value,
                    };
                    const releaseTagPatternIsValid = {
                        ...this.state.releaseTagPatternIsValid,
                        [id]: isValid,
                    };
                    this.setState({
                        releaseTagPatternUpdated,
                        releaseTagPatternIsValid,
                    });
                }}
            />
        );
    };

    renderReleaseTagPatternActions: (record: ReleaseTagPattern) => JSX.Element = (record) => {
        const releaseTagEditing = this.state.releaseTagIsEditing[record.id];
        const releaseTagPatternIsValid = this.state.releaseTagPatternIsValid[record.id];

        return (
            <AppSettingsActionsComponent
                recordId={record.id}
                isEditing={releaseTagEditing}
                saveDisabled={!releaseTagPatternIsValid}
                saveIsLoading={this.props.releaseTagPatternSaveIsLoading[record.id]}
                deleteConfirmationText="Are you sure you want to delete this release tag pattern?"
                saveButtonOnClick={(id) => this.saveReleaseTagPattern(id as number)}
                editCancelButtonClicked={(id) => {
                    const releaseTagIsEditing = {
                        ...this.state.releaseTagIsEditing,
                        [id]: false,
                    };

                    const releaseTagPatternUpdated = {
                        ...this.state.releaseTagPatternUpdated,
                        [id]: null,
                    };
                    const releaseTagPatternIsValid = {
                        ...this.state.releaseTagPatternIsValid,
                        [id]: null,
                    };

                    this.setState({
                        releaseTagIsEditing,
                        releaseTagPatternUpdated,
                        releaseTagPatternIsValid,
                    });
                }}
                editStartButtonClicked={(id) => {
                    const releaseTagIsEditing = {
                        ...this.state.releaseTagIsEditing,
                        [id]: true,
                    };
                    this.setState({ releaseTagIsEditing });
                }}
                deleteConfirm={(id) => this.confirmReleaseTagPatternDelete(id as number)}
                editPermissionedActionType={RunwayUserAction.UAAddRemoveEditReleaseTagPatterns}
                deletePermissionsedActionType={RunwayUserAction.UAAddRemoveEditReleaseTagPatterns}
            />
        );
    };

    render() {
        return (
            <div ref={this.props.refProps} style={this.props.style}>
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                        <Space direction="vertical" size="small">
                            <h4 style={styles.appSettingsSectionTitleStyle}>Release tag pattern</h4>
                            <Text type="secondary">
                                Read more about tokenized patterns in our{' '}
                                <Typography.Link
                                    href="https://docs.runway.team/getting-started/pattern-strings-tokens"
                                    target="_blank"
                                >
                                    docs.
                                </Typography.Link>
                            </Text>
                        </Space>
                        <PermissionedActionButton
                            actionType={RunwayUserAction.UAAddRemoveEditReleaseTagPatterns}
                            style={this.props.releaseTagPatterns.length > 0 ? {} : { float: 'right' }}
                            icon={<PlusOutlined />}
                            onClick={this.onAddNewReleaseTagClicked}
                            disabled={this.props.releaseTagPatterns.length > 0}
                        >
                            Add
                        </PermissionedActionButton>
                    </div>

                    <Table
                        style={{ marginTop: 12, marginBottom: 12 }}
                        size="small"
                        locale={{
                            emptyText: 'No release tag pattern added yet',
                        }}
                        columns={[
                            {
                                title: 'Pattern',
                                render: this.renderReleaseTagPatternField,
                                width: 500,
                            },
                            {
                                title: '',
                                render: this.renderReleaseTagPatternActions,
                            },
                        ]}
                        dataSource={this.props.releaseTagPatterns.map((releaseTagPattern, index) => {
                            return {
                                key: index,
                                ...releaseTagPattern,
                            };
                        })}
                        pagination={false}
                        bordered
                    />
                </div>
                <NewReleaseTagPatternModal
                    open={this.state.newReleaseTagModalVisible}
                    isLoading={this.props.newReleaseTagPatternIsLoading}
                    onCancelAddNewReleaseTagPattern={this.onCancelAddNewReleaseTag}
                    onOkNewReleaseTagPattern={this.addNewReleaseTag}
                    suggestedTagPattern={this.state.suggestedTagPattern}
                    suggestedTagPatternIsLoading={this.state.isSuggestedTagPatternLoading}
                />
            </div>
        );
    }
}

interface ReleaseTagPatternsComponentProps extends AppComponentProps {
    refProp?: React.MutableRefObject<HTMLDivElement>;
    style?: React.CSSProperties;
}

const mapProps = (state: RootState, ownProps: ReleaseTagPatternsComponentProps) => {
    const { appId, orgId } = ownProps;

    const app = selectApp(state, appId);
    const newReleaseTagPatternIsLoading = getIsLoading(AppSettingsUpdatable.NewReleaseTagPattern, state);
    const newReleaseTagPatternAdded = getUpdated(AppSettingsUpdatable.NewReleaseTagPattern, state);
    const newReleaseTagPatternError = getError(AppSettingsUpdatable.NewReleaseTagPattern, state);

    const credentials = selectAuthCredentials(state);

    const releaseTagPatternSaveIsLoading: NumberKeyedDictionary<boolean> = {};
    const releaseTagPatternUpdated: NumberKeyedDictionary<boolean> = {};
    const releaseTagPatternUpdateError: NumberKeyedDictionary<string> = {};

    forEach(app.appSettings.releaseTagPatterns, (releaseTagPattern) => {
        const saveIsLoading = getIsLoading(AppSettingsUpdatable.UpdateReleaseTagPattern, state, releaseTagPattern.id);
        const updated = getUpdated(AppSettingsUpdatable.UpdateReleaseTagPattern, state, releaseTagPattern.id);
        const error = getError(AppSettingsUpdatable.UpdateReleaseTagPattern, state, releaseTagPattern.id);

        releaseTagPatternSaveIsLoading[releaseTagPattern.id] = saveIsLoading;
        releaseTagPatternUpdated[releaseTagPattern.id] = updated;
        releaseTagPatternUpdateError[releaseTagPattern.id] = error;
    });

    return {
        appName: app.appSettings.appName,
        releaseTagPatterns: app.appSettings.releaseTagPatterns,
        newReleaseTagPatternIsLoading,
        newReleaseTagPatternAdded,
        newReleaseTagPatternError,
        releaseTagPatternUpdated,
        releaseTagPatternSaveIsLoading,
        releaseTagPatternUpdateError,
        credentials,
        appId,
        orgId,
        refProps: ownProps.refProp,
        style: ownProps.style,
    };
};

const mapDispatch = (dispatch) => {
    return {
        addNewReleaseTag: (request: NewReleaseTagPatternRequest) => {
            dispatch(tryUpdateAppSettings(AppSettingsUpdatable.NewReleaseTagPattern, request));
        },
        updateReleaseTagPattern: (request: UpdateReleaseTagPatternRequest, id: number) => {
            dispatch(tryUpdateAppSettings(AppSettingsUpdatable.UpdateReleaseTagPattern, request, id));
        },
        deleteReleaseTagPattern: (request: DeleteReleaseTagPatternRequest) => {
            dispatch(tryUpdateAppSettings(AppSettingsUpdatable.DeleteReleaseTagPattern, request));
        },
    };
};

const connector = connect(mapProps, mapDispatch);

type Props = ConnectedProps<typeof connector>;

export default connector(ReleaseTagPatternsComponent);
